import { Input } from 'antd';
import { useState } from 'react';

import { ImageAttach, type AttachedImage } from '@/components/common/image-attach';
import { Select } from '@/components/common/select';
import { useConfirm } from '@/components/feedback/dialog';
import { Modal } from '@/components/feedback/modal';
import type { Device } from '@/features/devices';

import {
  DESC_MAX,
  DESC_MIN,
  MAX_PHOTOS,
  REASON_OTHER_MAX,
  REASON_OTHER_MIN,
  WARRANTY_REASONS,
} from '../constants';
import { useCreateWarrantyClaim } from '../hooks';
import type { WarrantyClaim, WarrantyReason } from '../types';

import styles from './warranty-claim-modal.module.scss';

export type WarrantyClaimModalProps = {
  open: boolean;
  /** Devices the grower can claim against. One entry (locked) when launched from detail. */
  devices: Device[];
  /** When set, the device selector is locked to this id (device-detail entry point). */
  lockedDeviceId?: string;
  onClose: () => void;
  /** Fired with the created claim so the flow can show the success dialog. */
  onSubmitted: (claim: WarrantyClaim) => void;
  /** Fired when submission fails a server-side check (shows the failure dialog). */
  onFailed: () => void;
};

function Label({ children, required }: { children: string; required?: boolean }) {
  return (
    <span className={styles.label}>
      {children}
      {required ? <span className={styles.req}> *</span> : null}
    </span>
  );
}

/**
 * Warranty Claim form (Figma 2389-752622). Device (locked from detail, selectable from the
 * listing), Reason dropdown (a custom field becomes mandatory for "Other", 10–500 chars),
 * Description (20–2000 chars), and 1–3 required JPG/PNG photos. Per AC the Submit button is
 * always actionable and validates on click — inline errors surface and block submission;
 * the backend re-checks eligibility (story 101871).
 */
export function WarrantyClaimModal({
  open,
  devices,
  lockedDeviceId,
  onClose,
  onSubmitted,
  onFailed,
}: WarrantyClaimModalProps) {
  const { confirm } = useConfirm();

  const defaultDeviceId = lockedDeviceId ?? devices[0]?.id ?? null;
  const [deviceId, setDeviceId] = useState<string | null>(defaultDeviceId);
  const [reason, setReason] = useState<WarrantyReason | null>(null);
  const [reasonOther, setReasonOther] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [tried, setTried] = useState(false);

  const create = useCreateWarrantyClaim(deviceId ?? undefined);

  // Reset to a fresh form whenever the modal (re)opens (no draft persistence).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDeviceId(defaultDeviceId);
      setReason(null);
      setReasonOther('');
      setDescription('');
      setImages([]);
      setTried(false);
    }
  }

  const otherLen = reasonOther.trim().length;
  const descLen = description.trim().length;
  const uploading = images.some((img) => img.status === 'uploading');

  const deviceValid = deviceId != null;
  const reasonValid = reason != null;
  const otherValid =
    reason !== 'other' || (otherLen >= REASON_OTHER_MIN && otherLen <= REASON_OTHER_MAX);
  const descValid = descLen >= DESC_MIN && descLen <= DESC_MAX;
  const photosValid = images.length >= 1 && !uploading;
  const valid = deviceValid && reasonValid && otherValid && descValid && photosValid;

  const dirty =
    reason != null || reasonOther.length > 0 || description.length > 0 || images.length > 0;

  const deviceLocked = lockedDeviceId != null;
  const selectedDevice = devices.find((d) => d.id === deviceId);
  const deviceOptions = devices.map((d) => ({ value: d.id, label: d.name }));

  const submit = () => {
    setTried(true);
    if (!valid || create.isPending || reason == null || deviceId == null) return;
    create.mutate(
      {
        deviceId,
        reason,
        reasonOther: reason === 'other' ? reasonOther.trim() : null,
        description: description.trim(),
        images: images.map((img) => img.name),
      },
      {
        onSuccess: (claim) => onSubmitted(claim),
        onError: () => onFailed(),
      },
    );
  };

  const requestClose = () => {
    if (create.isPending) return;
    if (!dirty) {
      onClose();
      return;
    }
    void confirm({
      title: 'Discard Claim?',
      subtitle: 'Your claim details will be lost if you leave.',
      confirmText: 'Leave',
      cancelText: 'Stay',
      danger: true,
    }).then((leave) => {
      if (leave) onClose();
    });
  };

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title="Claim Warranty"
      size="lg"
      loading={create.isPending}
      primaryText="Submit"
      primaryVariant="neutral"
      onPrimary={submit}
      cancelText="Cancel"
    >
      <div className={styles.form}>
        <div className={styles.row2}>
          <div className={styles.field}>
            <Label required>Selected Device</Label>
            <Select
              disabled={deviceLocked}
              value={deviceLocked ? (selectedDevice?.name ?? deviceId) : deviceId}
              options={
                deviceLocked
                  ? [{ value: deviceId ?? '', label: selectedDevice?.name ?? '' }]
                  : deviceOptions
              }
              placeholder="Select a device"
              status={tried && !deviceValid ? 'error' : undefined}
              onChange={(value) => setDeviceId(value as string)}
              aria-label="Selected device"
            />
            {tried && !deviceValid ? (
              <span className={styles.error}>Please select a device</span>
            ) : null}
          </div>

          <div className={styles.field}>
            <Label required>Reason</Label>
            <Select
              value={reason}
              options={WARRANTY_REASONS}
              placeholder="Select"
              status={tried && !reasonValid ? 'error' : undefined}
              onChange={(value) => setReason(value as WarrantyReason)}
              aria-label="Reason"
            />
            {tried && !reasonValid ? (
              <span className={styles.error}>Please select a reason</span>
            ) : null}
          </div>
        </div>

        {reason === 'other' ? (
          <label className={styles.field}>
            <Label required>Please specify</Label>
            <Input
              value={reasonOther}
              maxLength={REASON_OTHER_MAX}
              placeholder="Describe the reason"
              status={tried && !otherValid ? 'error' : undefined}
              onChange={(e) => setReasonOther(e.target.value)}
            />
            {tried && !otherValid ? (
              <span className={styles.error}>
                Please specify the reason ({REASON_OTHER_MIN}-{REASON_OTHER_MAX} characters)
              </span>
            ) : null}
          </label>
        ) : null}

        <label className={styles.field}>
          <Label>Description</Label>
          <Input.TextArea
            value={description}
            maxLength={DESC_MAX}
            rows={3}
            placeholder="Enter description"
            status={tried && !descValid ? 'error' : undefined}
            onChange={(e) => setDescription(e.target.value)}
          />
          {tried && !descValid ? (
            <span className={styles.error}>
              Description must be between {DESC_MIN} and {DESC_MAX} characters
            </span>
          ) : null}
        </label>

        <div className={styles.field}>
          <ImageAttach
            value={images}
            onChange={setImages}
            maxCount={MAX_PHOTOS}
            invalid={tried && images.length === 0}
          />
          {tried && images.length === 0 ? (
            <span className={styles.error}>At least one photo is required</span>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
